<template>
    <component :is="tag">
        <slot
            :brief="brief"
            :task="task"
            :content="content"
            :is-loading="isLoading"
            :is-submitting="isSubmitting"
            :errors="errors"
            :error-message="errorMessage"
            :upload-screenshot-url="uploadScreenshotUrl"
            :handle-file-change="handleFileChange"
            :handle-reset-uploads="handleResetUploads"
            :upload-headers="uploadHeaders"
            :uploaded-files="uploadedFiles"
            :is-file-uploading="isFileUploading"
            :is-file-deleting="isFileDeleting"
            :back="handleBack"
            :update-content-metrics="handleUpdateContentMetrics"
            :handle-screenshots-submitted="handleScreenshotsSubmitted"
            :handle-file-deleted="handleFileDeleted"
            :handle-before-upload-validation="handleBeforeUploadValidation"
        />
    </component>
</template>

<script>
    import { computed } from 'vue-demi';
    import { message } from '@grininc/grin-ui';
    import { useContactStore, useFeatureFlagsStore } from '../../../../stores';
    import useTaskDelivery from '../../data-views/activation-briefs/composables/use-task-delivery';
    import useContentMetrics from '../../../../src/composables/use-content-metrics';
    import ActivationBriefTasksMixin from '../mixins/ActivationBriefTasksMixin';
    import BaseActivationBriefStepMixin from '../mixins/BaseActivationBriefStepMixin';
    import ContactsMixin from '../mixins/ContactsMixin';
    import UploadMixin from '../mixins/UploadMixin';

    export default {
        name: 'ActivationContentMetricsProvider',
        mixins: [ContactsMixin, UploadMixin, BaseActivationBriefStepMixin, ActivationBriefTasksMixin],

        data() {
            return {
                content: null,
                isLoading: false,
                isSubmitting: false,
                isFileUploading: false,
                isFileDeleting: false
            };
        },

        setup() {
            const contact = useContactStore().data;
            const featureFlags = useFeatureFlagsStore().data;
            const { goToDeliveryPage } = useTaskDelivery();
            const { getContentByIdAndContactId, updateContentMetrics, postScreenshots, deleteScreenshotFile } =
                useContentMetrics();

            const showCreatorContentMetrics = computed(() => {
                return featureFlags['creator-content-metric-screenshots'] || false;
            });

            const getContent = (id) => {
                return getContentByIdAndContactId(id, contact.id);
            };

            return {
                showCreatorContentMetrics,
                goToDeliveryPage,
                getContent,
                updateContentMetrics,
                postScreenshots,
                deleteScreenshotFile
            };
        },

        computed: {
            uploadScreenshotUrl() {
                return `/contact/${this.contactId}/content/${this.contentParamId}/content-metric-screenshot/upload`;
            },
            isTasklessContent() {
                // Check if this is taskless content (no task_id in route)
                return !this.$route.params.task_id;
            }
        },

        methods: {
            handleBack() {
                if (this.isTasklessContent) {
                    // For taskless content, go back to the main activation brief page
                    this.$router.push({
                        name: 'v2.activation.brief',
                        params: {
                            brief_id: this.$route.params.brief_id
                        }
                    });
                } else {
                    // For task-based content, go back to task content page
                    this.$router.push({ name: 'v2.activation.brief.task.content' });
                }
            },
            async handleUpdateContentMetrics(id, input) {
                this.isSubmitting = true;

                try {
                    this.content = await this.updateContentMetrics(id, this.contactId, input);
                    message.success('Metrics updated successfully');
                    this.handleBack();
                } catch (error) {
                    this.handleHttpError(error);
                }

                this.isSubmitting = false;
            },
            handleFileChange(info) {
                if (!this.handleBeforeUploadValidation(info?.file, null, true)) {
                    return;
                }

                this.isFileUploading = true;
                this.handleUploadChange(
                    info,
                    () => (this.isFileUploading = false),
                    () => {
                        this.handleUploadError(info);
                        this.isFileUploading = false;
                    },
                    true
                );
            },
            handleResetUploads() {
                this.handleResetUploadedFiles();
            },
            async handleScreenshotsSubmitted(payload) {
                this.isSubmitting = true;

                try {
                    await this.postScreenshots(this.content?.id, this.contactId, payload);
                    message.success('Your screenshots have been submitted and are awaiting processing.');
                    this.handleResetUploads();
                    this.handleBack();
                } catch (error) {
                    this.handleHttpError(error);
                }

                this.isSubmitting = false;
            },
            async handleFileDeleted(fileName) {
                this.isFileDeleting = true;

                try {
                    await this.deleteScreenshotFile(this.content?.id, this.contactId, fileName);
                    this.handleFileDelete(fileName);
                    message.success('Your file has been deleted.');
                } catch (error) {
                    this.handleHttpError(error);
                }

                this.isFileDeleting = false;
            },
            handleBeforeUploadValidation(file, _, validationOnly = false) {
                const validated = this.handleBeforeUpload(file, validationOnly);

                if (!validated) {
                    this.isFileUploading = false;
                }

                return validated;
            }
        },

        async created() {
            this.isLoading = true;

            try {
                await this.loadContact(true);
                await this.loadActivationBrief(true);
                this.content = await this.getContent(this.contentParamId);

                if (!this.showCreatorContentMetrics) {
                    this.isLoading = false; // Stop loading before redirect

                    if (this.isTasklessContent) {
                        // For taskless content, redirect to main activation brief page
                        this.$router.push({
                            name: 'v2.activation.brief',
                            params: {
                                brief_id: this.$route.params.brief_id
                            }
                        });
                        return; // Exit early to prevent further execution
                    } else {
                        // For task-based content, use existing delivery logic
                        // Only call goToDeliveryPage if we have a valid task
                        if (this.task) {
                            this.goToDeliveryPage(this.task);
                        } else {
                            // Fallback to main activation brief page if task is not found
                            this.$router.push({
                                name: 'v2.activation.brief',
                                params: {
                                    brief_id: this.$route.params.brief_id
                                }
                            });
                        }
                        return; // Exit early to prevent further execution
                    }
                }
            } catch (error) {
                this.handleHttpError(error);
            }

            this.isLoading = false;
        }
    };
</script>
